import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PopupOverlay, { Anchors } from '../Popovers/PopupOverlay';
import { useMap } from 'rwl-ui';
import { When } from 'react-if';
import { useMapContext } from '../../../hooks';

interface BaseMarkerProps {
    position: google.maps.LatLngLiteral;
    markerContent: React.ReactNode;
    popoverContent?: React.ReactNode;
    popoverAnchor?: Anchors;
    closeDelay?: number;
    onMarkerClick?: () => void;
    onMarkerHover?: (state: HoverState) => void;
}

type MarkerStates = {
    isMarkerHovered: boolean;
    isPopoverHovered: boolean;
    isPopupOpen: boolean;
};

type TimeoutType = ReturnType<typeof setTimeout>;
export type HoverState = 'enter' | 'exit';

export const BaseMarker = ({
    position,
    markerContent,
    popoverContent,
    popoverAnchor = 'bestCenter',
    closeDelay = 500,
    onMarkerClick,
    onMarkerHover,
}: BaseMarkerProps) => {
    const { map } = useMap();
    const { openMarkerId, setOpenMarkerId } = useMapContext();
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
        null
    );
    const markerContainerRef = useRef<HTMLDivElement>(
        document.createElement('div')
    );

    const [markerState, setMarkerState] = useState<MarkerStates>({
        isMarkerHovered: false,
        isPopoverHovered: false,
        isPopupOpen: false,
    });

    const closeTimeoutIdRef = useRef<TimeoutType | null>(null);

    useEffect(() => {
        if (
            openMarkerId &&
            openMarkerId !== markerRef.current?.id &&
            markerState.isPopupOpen
        ) {
            closePopup();
        }
    }, [openMarkerId]);

    useEffect(() => {
        let isMounted = true;

        const createMarker = async () => {
            if (!map || !window.google || !isMounted) return;
            const { AdvancedMarkerElement } = (await google.maps.importLibrary(
                'marker'
            )) as {
                AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
            };

            if (!markerRef.current) {
                markerRef.current = new AdvancedMarkerElement({
                    map,
                    position: new google.maps.LatLng(position),
                    content: markerContainerRef.current,
                });

                markerRef.current.id = crypto.randomUUID();

                if (onMarkerClick && onMarkerHover) {
                    markerRef.current.element?.addEventListener(
                        'mouseenter',
                        handleMarkerMouseEnter
                    );
                    markerRef.current.element?.addEventListener(
                        'mouseleave',
                        handleMarkerMouseLeave
                    );
                    markerRef.current.addListener('click', handleMarkerClick);
                }
            } else {
                markerRef.current.map = map;
                markerRef.current.position = new google.maps.LatLng(position);
            }
        };

        createMarker();
        return () => {
            isMounted = false;
            if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
            }
        };
    }, [map, position]);

    const handleMarkerMouseEnter = () => {
        onMarkerHover?.('enter');
        setMarkerState((prev) => ({ ...prev, isMarkerHovered: true }));
        cancelClose();
        openPopup();
    };

    const handleMarkerMouseLeave = () => {
        onMarkerHover?.('exit');
        setMarkerState((prev) => ({ ...prev, isMarkerHovered: false }));
        scheduleClose();
    };

    const handleMarkerClick = () => {
        setMarkerState((prev) => ({ ...prev, isPopupOpen: !prev.isPopupOpen }));
        onMarkerClick?.();
    };

    const handlePopoverMouseEnter = () => {
        setMarkerState((prev) => ({ ...prev, isPopoverHovered: true }));
        cancelClose();
    };

    const handlePopoverMouseLeave = () => {
        setMarkerState((prev) => ({ ...prev, isPopoverHovered: false }));
        scheduleClose();
    };

    const openPopup = () => {
        setOpenMarkerId(markerRef.current?.id);
        setMarkerState((prev) => ({ ...prev, isPopupOpen: true }));
    };

    const closePopup = () => {
        setMarkerState((prev) => ({ ...prev, isPopupOpen: false }));
    };

    const scheduleClose = () => {
        if (closeTimeoutIdRef.current) clearTimeout(closeTimeoutIdRef.current);
        closeTimeoutIdRef.current = setTimeout(() => {
            // Only close if neither marker nor popover is hovered
            setMarkerState((prev) => {
                if (!prev.isMarkerHovered && !prev.isPopoverHovered)
                    closePopup();
                return prev;
            });
        }, closeDelay);
    };

    const cancelClose = () => {
        if (closeTimeoutIdRef.current) {
            clearTimeout(closeTimeoutIdRef.current);
            closeTimeoutIdRef.current = null;
        }
    };

    return (
        <>
            <BaseMarkerPortal
                markerContent={markerContent}
                markerContainerRef={markerContainerRef}
            />
            <BasePopOverPortal
                handlePopoverMouseEnter={handlePopoverMouseEnter}
                handlePopoverMouseLeave={handlePopoverMouseLeave}
                isOpen={markerState.isPopupOpen}
                markerContainerRef={markerContainerRef}
                popoverAnchor={popoverAnchor}
                popoverContent={popoverContent}
                position={position}
            />
        </>
    );
};

interface BaseMarkerPortalProps {
    markerContent: React.ReactNode;
    markerContainerRef: React.MutableRefObject<HTMLDivElement>;
}

const BaseMarkerPortal = ({
    markerContent,
    markerContainerRef,
}: BaseMarkerPortalProps) => {
    return createPortal(markerContent, markerContainerRef.current);
};

interface BasePopOverPortalProps {
    handlePopoverMouseEnter: () => void;
    handlePopoverMouseLeave: () => void;
    isOpen: boolean;
    markerContainerRef: React.MutableRefObject<HTMLDivElement>;
    popoverAnchor: Anchors;
    popoverContent: React.ReactNode;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
}

const BasePopOverPortal = ({
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    isOpen,
    markerContainerRef,
    popoverAnchor,
    popoverContent,
    position,
}: BasePopOverPortalProps) => {
    const { map } = useMap();
    return (
        <When condition={isOpen}>
            <PopupOverlay
                map={map}
                position={position}
                anchor={popoverAnchor}
                markerElement={markerContainerRef.current}
            >
                <div
                    onMouseEnter={handlePopoverMouseEnter}
                    onMouseLeave={handlePopoverMouseLeave}
                >
                    {popoverContent}
                </div>
            </PopupOverlay>
        </When>
    );
};
